import { FC, memo } from 'react';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName, IconColorType } from '@frontend/icons';
import { PopoverMenu, usePopoverMenu, Text, FontColorType } from '@frontend/design-system';
import { taskCenterTrackingIds } from '../../../tracking-ids';
import { PopoverMenuItems } from '../../common';

interface TaskItemPopoverMenuProps {
  status: TaskCenterTypes.TaskStatus;
  updateTaskStatus: (status: TaskCenterTypes.TaskStatus) => void;
  deleteTask: () => void;
  getItemProps: ReturnType<typeof usePopoverMenu<HTMLElement>>['getItemProps'];
  getMenuProps: ReturnType<typeof usePopoverMenu<HTMLElement>>['getMenuProps'];
  onClickEdit: () => void;
}

type PopoverOptionsType = {
  iconName: IconName;
  iconColor?: IconColorType;
  labelColor?: FontColorType;
  onClick: () => void;
  label: string;
  trackingId: string;
  hide?: boolean;
};

export const TaskItemPopoverMenu: FC<TaskItemPopoverMenuProps> = memo(
  ({ status, updateTaskStatus, deleteTask, getMenuProps, getItemProps, onClickEdit }) => {
    const { t } = useTranslation('task-center');

    const popoverOptions: PopoverOptionsType[] = [
      {
        iconName: 'check',
        label: t('Mark as Done'),
        onClick: () => updateTaskStatus(TaskCenterTypes.TaskStatus.STATUS_COMPLETED),
        trackingId: taskCenterTrackingIds.markAsDone,
        hide: status === TaskCenterTypes.TaskStatus.STATUS_COMPLETED,
      },
      {
        iconName: 'pending',
        label: t('Move to To Do'),
        onClick: () => updateTaskStatus(TaskCenterTypes.TaskStatus.STATUS_TODO),
        trackingId: taskCenterTrackingIds.markAsTodo,
        hide: status === TaskCenterTypes.TaskStatus.STATUS_TODO,
      },
      {
        iconName: 'in-progress-small',
        label: t('Move to In Progress'),
        onClick: () => updateTaskStatus(TaskCenterTypes.TaskStatus.STATUS_IN_PROGRESS),
        trackingId: taskCenterTrackingIds.markAsInProgress,
        hide: status === TaskCenterTypes.TaskStatus.STATUS_IN_PROGRESS,
      },
      {
        iconName: 'edit',
        label: t('Edit'),
        onClick: onClickEdit,
        trackingId: taskCenterTrackingIds.editTaskFromList,
      },
      {
        iconName: 'trash',
        label: t('Delete Task'),
        onClick: deleteTask,
        trackingId: taskCenterTrackingIds.deleteTaskFromList,
        iconColor: 'error',
        labelColor: 'error',
      },
    ];

    return (
      <PopoverMenu {...getMenuProps()}>
        <PopoverMenuItems<PopoverOptionsType>
          data={popoverOptions}
          renderItem={(item) => (
            <>
              <Icon name={item.iconName} color={item.iconColor ?? 'default'} size={16} />{' '}
              <Text color={item.labelColor ?? 'default'}>{item.label}</Text>
            </>
          )}
          canRender={(item) => !item.hide}
          keyGen={(item) => `task-item${item.trackingId}`}
          getItemProps={getItemProps}
          onClickItem={(item) => item.onClick()}
          trackingId={(item) => item.trackingId}
        />
      </PopoverMenu>
    );
  }
);

TaskItemPopoverMenu.displayName = 'TaskItemPopoverMenu';
